import { forwardRef, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import { useStatsigClient } from '@statsig/react-bindings';

import type { Question, QuestionAnswer, QuestionProperties } from '@api/instant_survey/types';
import { GATES } from '@constants/statsig';
import {
  Button,
  Checkbox,
  Fieldset,
  Layout,
  MinusPlusButtons,
  Overlay,
  Pill,
  Popover,
} from '@utilities';
import type { PopoverProps } from '@utilities/components/Popover';

import styles from './_index.module.scss';

interface MaxAnswersProps {
  question: Question;
  updateQuestionProperties: (properties: Partial<QuestionProperties>) => void;
}

const MaxAnswers = ({ question, updateQuestionProperties }: MaxAnswersProps) => {
  const pillRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLSpanElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleApply = useCallback(
    (propertiesToApply: Partial<QuestionProperties>) => {
      updateQuestionProperties({ ...propertiesToApply });
      setIsPopoverOpen(false);
    },
    [updateQuestionProperties]
  );

  const handleClear = () => {
    updateQuestionProperties({ maxAnswers: undefined, mustBeMax: undefined });
    setIsPopoverOpen(false);
  };

  return (
    <>
      <Layout.Flex className={styles['max-answers']} gap="x-small">
        <span ref={popoverRef}>Max Answers</span>
        <Pill
          buttonRef={pillRef}
          data-testid="max-answers-pill"
          isModified={!!question.properties?.maxAnswers}
          onClick={() => setIsPopoverOpen(!isPopoverOpen)}
          onClose={handleClear}
          value={question.properties?.maxAnswers ?? 'Any Number'}
        />
      </Layout.Flex>
      <Overlay
        container={popoverRef.current}
        onHide={() => setIsPopoverOpen(false)}
        placement="bottom"
        rootClose
        show={isPopoverOpen}
        target={pillRef.current}
      >
        <MaxAnswersPopover onApply={handleApply} question={question} />
      </Overlay>
    </>
  );
};

interface MaxAnswersPopoverProps extends PopoverProps {
  onApply: (properties: Partial<QuestionProperties>) => void;
  // TODO: noneAnswer is added in the ModalInstantSurveyQuestion component
  question: Question & { noneAnswer?: QuestionAnswer };
}

const MaxAnswersPopover = forwardRef<HTMLDivElement, MaxAnswersPopoverProps>(
  ({ className, onApply, question, ...props }: MaxAnswersPopoverProps, ref) => {
    const { client } = useStatsigClient();
    const useCalypsoProvider = client.checkGate(GATES.USE_CALYPSO_AS_SURVEY_PROVIDER);
    const [propertiesToApply, setPropertiesToApply] = useState({
      ...question.properties,
      maxAnswers: question.properties?.maxAnswers ?? 1,
      mustBeMax: question.properties?.mustBeMax ?? false,
    });

    const isApplyDisabled = isEqual(question.properties, propertiesToApply);
    const maxSelectedAnswers = question.answers.length + (question.noneAnswer ? 1 : 0);

    return (
      <Popover
        className={classNames(className, styles['max-answers-popover'])}
        id="max-answers-popover"
        ref={ref}
        {...props}
      >
        <Popover.Body>
          <Layout.Flex gap="x-small">
            <Layout.Fill>Max Answers</Layout.Fill>
            <strong className={styles['max-answers-value']}>
              {propertiesToApply.maxAnswers ?? 1}
            </strong>
            <MinusPlusButtons
              disableMinusButton={(propertiesToApply.maxAnswers ?? 1) <= 1}
              disablePlusButton={(propertiesToApply.maxAnswers ?? 1) >= maxSelectedAnswers}
              onClickMinus={() =>
                setPropertiesToApply((prev) => ({
                  ...prev,
                  maxAnswers: (prev.maxAnswers ?? 1) - 1,
                }))
              }
              onClickPlus={() =>
                setPropertiesToApply((prev) => ({
                  ...prev,
                  maxAnswers: (prev.maxAnswers ?? 1) + 1,
                }))
              }
            />
          </Layout.Flex>
          {useCalypsoProvider && (
            <>
              <hr className={styles['max-answers-popover-divider']} />
              <Fieldset legend="Additional Options">
                <Checkbox
                  className={styles['max-answers-checkbox']}
                  isChecked={propertiesToApply.mustBeMax}
                  label="Must select max answers"
                  onChange={() =>
                    setPropertiesToApply((prev) => ({
                      ...prev,
                      mustBeMax: !prev.mustBeMax,
                    }))
                  }
                  value={propertiesToApply.mustBeMax}
                />
              </Fieldset>
            </>
          )}
          <Button
            data-testid="max-answers-apply-button"
            isDisabled={isApplyDisabled}
            onClick={() => onApply(propertiesToApply)}
            text="Apply"
          />
        </Popover.Body>
      </Popover>
    );
  }
);

MaxAnswersPopover.displayName = 'MaxAnswersPopover';

export default MaxAnswers;
